import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { CatDropdown } from "blocks-nextjs/blocks-react/components/Dropdown/Dropdown";
import { CatList } from "blocks-nextjs/blocks-react/components/List/List";
import { CatListItem } from "blocks-nextjs/blocks-react/components/ListItem/ListItem";
import React from "react";
import useProjectDetailsStore from "../../store/useProjectDetailsStore";
import { formatDateWithTimezone } from "../../utils/dateFormatter";
const MS_PER_MINUTE = 60000;
const MINUTES_PER_HOUR = 60;
const LAST_7_DAYS_OFFSET = 6;
const LAST_30_DAYS_OFFSET = 29;
const DAYS_PER_WEEK = 7;
const DATE_RANGE_TODAY = "Today";
const DATE_RANGE_YESTERDAY = "Yesterday";
const DATE_RANGE_CURRENT_WEEK = "CurrentWeek";
const DATE_RANGE_PREVIOUS_WEEK = "PreviousWeek";
const DATE_RANGE_LAST_7_DAYS = "Last7Days";
const DATE_RANGE_LAST_30_DAYS = "Last30Days";
const DATE_RANGE_CURRENT_MONTH = "CurrentMonth";
const DATE_RANGE_CUSTOM_DATE = "CustomDate";
function getDateInTimezone(timezoneOffset) {
    const now = new Date();
    const offsetStr = timezoneOffset.replace("UTC", "");
    const offsetMatch = offsetStr.match(/([+-])(\d{1,2}):?(\d{2})?/);
    if (offsetMatch) {
        const sign = offsetMatch[1] === "+" ? 1 : -1;
        const hours = parseInt(offsetMatch[2], 10);
        const minutes = parseInt(offsetMatch[3] || "0", 10);
        const totalOffsetMinutes = sign * (hours * MINUTES_PER_HOUR + minutes);
        const utcTime = now.getTime() + now.getTimezoneOffset() * MS_PER_MINUTE;
        const projectTime = new Date(utcTime + totalOffsetMinutes * MS_PER_MINUTE);
        return projectTime;
    }
    return now;
}
const DateRangeDropdown = ({ className = "w-[25rem]", isLastSevenDaysOptionOnly = false, onValueChange, placeholder = "Sorting option(s)", selectedValue = DATE_RANGE_LAST_7_DAYS, size = "sm" }) => {
    // Get timezone offset from project metadata
    const { projectMetaData } = useProjectDetailsStore((state) => ({
        projectMetaData: state.projectMetaData
    }));
    const timezoneOffset = projectMetaData?.offset || "UTC+00:00";
    const today = getDateInTimezone(timezoneOffset);
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);
    const last7DaysStart = new Date(today);
    last7DaysStart.setDate(today.getDate() - LAST_7_DAYS_OFFSET);
    const last30DaysStart = new Date(today);
    last30DaysStart.setDate(today.getDate() - LAST_30_DAYS_OFFSET);
    const currentWeekStart = new Date(today);
    currentWeekStart.setDate(today.getDate() - today.getDay()); // Sunday
    // Current week ends today, not on Saturday
    const currentWeekEnd = new Date(today);
    const previousWeekStart = new Date(currentWeekStart);
    previousWeekStart.setDate(currentWeekStart.getDate() - DAYS_PER_WEEK);
    const previousWeekEnd = new Date(currentWeekStart);
    previousWeekEnd.setDate(currentWeekStart.getDate() - 1);
    const currentMonthStart = new Date(today.getFullYear(), today.getMonth(), 1);
    const formatDate = (date) => formatDateWithTimezone(date, timezoneOffset);
    const handleItemSelect = (value) => {
        if (onValueChange) {
            onValueChange(value);
        }
    };
    const getListItem = () => {
        return (_jsx(_Fragment, { children: !isLastSevenDaysOptionOnly ? (_jsxs(_Fragment, { children: [_jsx(CatListItem, { "data-test": "date-range-item-today", onBlSelect: () => handleItemSelect(DATE_RANGE_TODAY), value: {
                            label: `Today (${formatDate(today)})`,
                            value: DATE_RANGE_TODAY
                        } }), _jsx(CatListItem, { "data-test": "date-range-item-yesterday", onBlSelect: () => handleItemSelect(DATE_RANGE_YESTERDAY), value: {
                            label: `Yesterday (${formatDate(yesterday)})`,
                            value: DATE_RANGE_YESTERDAY
                        } }), _jsx(CatListItem, { "data-test": "date-range-item-current-week", onBlSelect: () => handleItemSelect(DATE_RANGE_CURRENT_WEEK), value: {
                            label: `Current Week (${formatDate(currentWeekStart)} - ${formatDate(currentWeekEnd)})`,
                            value: DATE_RANGE_CURRENT_WEEK
                        } }), _jsx(CatListItem, { "data-test": "date-range-item-previous-week", onBlSelect: () => handleItemSelect(DATE_RANGE_PREVIOUS_WEEK), value: {
                            label: `Previous Week (${formatDate(previousWeekStart)} - ${formatDate(previousWeekEnd)})`,
                            value: DATE_RANGE_PREVIOUS_WEEK
                        } }), _jsx(CatListItem, { "data-test": "date-range-item-last-7-days", onBlSelect: () => handleItemSelect(DATE_RANGE_LAST_7_DAYS), value: {
                            label: `Last 7 Days (${formatDate(last7DaysStart)} - ${formatDate(today)})`,
                            value: DATE_RANGE_LAST_7_DAYS
                        } }), _jsx(CatListItem, { "data-test": "date-range-item-last-30-days", onBlSelect: () => handleItemSelect(DATE_RANGE_LAST_30_DAYS), value: {
                            label: `Last 30 Days (${formatDate(last30DaysStart)} - ${formatDate(today)})`,
                            value: DATE_RANGE_LAST_30_DAYS
                        } }), _jsx(CatListItem, { "data-test": "date-range-item-current-month", onBlSelect: () => handleItemSelect(DATE_RANGE_CURRENT_MONTH), value: {
                            label: `Current Month (${formatDate(currentMonthStart)} - ${formatDate(today)})`,
                            value: DATE_RANGE_CURRENT_MONTH
                        } }), _jsx(CatListItem, { "data-test": "date-range-item-custom-date", onBlSelect: () => handleItemSelect(DATE_RANGE_CUSTOM_DATE), value: {
                            label: "Custom Date",
                            value: DATE_RANGE_CUSTOM_DATE
                        } })] })) : (_jsxs(_Fragment, { children: [_jsx(CatListItem, { "data-test": "date-range-item-last-7-days", onBlSelect: () => handleItemSelect(DATE_RANGE_LAST_7_DAYS), value: {
                            label: `Last 7 Days (${formatDate(last7DaysStart)} - ${formatDate(today)})`,
                            value: DATE_RANGE_LAST_7_DAYS
                        } }), _jsx(CatListItem, { "data-test": "date-range-item-custom-date", onBlSelect: () => handleItemSelect(DATE_RANGE_CUSTOM_DATE), value: {
                            label: "Custom Date",
                            value: DATE_RANGE_CUSTOM_DATE
                        } })] })) }));
    };
    return (_jsx(CatDropdown, { ariaDescribedBy: "date-range-dropdown-field", className: className, "data-test": "date-range-dropdown", fieldId: "date-range-dropdown-field", placeholder: placeholder, size: size, value: selectedValue, children: _jsx(CatList, { "data-test": "date-range-list", children: getListItem() }) }));
};
export default DateRangeDropdown;
